// src/pages/screens/TablesScreen.js
import React, { useEffect, useState } from "react";
import { useDatabase } from "../databaseContext";
import { Select, Table, Button, Modal, message } from "antd";
import { DatabaseOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AUTH_TOKEN, REACT_APP_API, ORG_ID, DOMAIN_ID } from "../../../constants";
import { AN_COLORS, AN_MONO, anAlpha } from "../../../design-system/tokens";
import {
  EmbeddedShell,
  FilterCard,
  FilterField,
  TableCard,
  anTableProps,
  tableEmpty,
} from "../../../design-system/tableUi";

const TablesScreen = ({
  showNotification,
  selectedDatabase: propSelectedDatabase,
  selectedHost: propSelectedHost,
}) => {
  const { dbNames, fetchDbNames, tablesMap, fetchTablesForDb, dbEndpoints } = useDatabase();
  const navigate = useNavigate();

  const [selectedVM, setSelectedVM] = useState(null);
  const [selectedDatabase, setSelectedDatabase] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedTables, setSelectedTables] = useState([]);
  const [fieldsMap, setFieldsMap] = useState({});
  const [vmList, setVmList] = useState([]);
  const [dbList, setDbList] = useState([]);
  const [tablesForSelectedDb, setTablesForSelectedDb] = useState([]);

useEffect(()=>{
  loadTables()
  // fetchUniqueVMs();
},[selectedDatabase])

  // Initialize with props
  useEffect(() => {
    if (dbEndpoints) {
      // setSelectedVM(dbEndpoints.map(db=>db.host));
      setVmList(dbEndpoints.map((db) => ({ value: db.host, label: db.host })));
    }
  }, [dbEndpoints]);

  // When VMs are loaded, check if our propSelectedHost exists in the list
  useEffect(() => {
    if (vmList.length > 0 && propSelectedHost) {
      const vmExists = vmList.some((vm) => vm.value === propSelectedHost);
      if (vmExists) {
        console.log("Setting selected VM from props:", propSelectedHost);
        setSelectedVM(propSelectedHost);
      } else {
        console.log("VM not found in list:", propSelectedHost);
      }
    }
  }, [vmList, propSelectedHost]);

  // When VM is selected, fetch its databases
  useEffect(() => {
    if (selectedVM) {
      console.log("Fetching databases for VM:", selectedVM);
      fetchDatabasesForVM(selectedVM);
    } else {
      setDbList([]);
      setSelectedDatabase(null);
    }
  }, [selectedVM]);

  // When dbList is updated and we have a propSelectedDatabase, try to select it
  useEffect(() => {
    if (dbList.length > 0 && propSelectedDatabase) {
      const dbExists = dbList.some((db) => db.value === propSelectedDatabase);
      if (dbExists) {
        console.log("Setting selected database from props:", propSelectedDatabase);
        setSelectedDatabase(propSelectedDatabase);
      } else {
        console.log("Database not found in list:", propSelectedDatabase);
      }
    }
  }, [dbList, propSelectedDatabase]);

  // Load tables when both VM and database are selected
  useEffect(() => {
    if (selectedVM && selectedDatabase) {
      console.log("Loading tables for VM:", selectedVM, "and DB:", selectedDatabase);
      loadTables(selectedDatabase);
      setSelectedTables([]);
    }
  }, [selectedVM, selectedDatabase]);

  // Fetch databases for selected VM
  const fetchDatabasesForVM = async (host) => {
    if (!host) {
      setDbList([]);
      return;
    }

    try {
      const payload = {
        orgId: ORG_ID(),
        tenantId: DOMAIN_ID(),
        filter: [
          {
            filterParameter: "host",
            filterValue: host,
          },
        ],
      };

      const res = await axios.post(
        `${REACT_APP_API}/api/v1/databaseService/getUniqueDatabasenames`,
        payload,
        {
          headers: {
            withCredentials: true,
            "X-Authorization": AUTH_TOKEN(),
          },
        }
      );

      if (res.data?.db_names) {
        const dbOptions = res.data.db_names.map((dbName) => {
          const dbNameWithoutHost = dbName.split(" (")[0];
          return {
            value: dbNameWithoutHost,
            label: dbNameWithoutHost,
          };
        });
        console.log("Fetched databases:", dbOptions);
        setDbList(dbOptions);
      }
    } catch (error) {
      console.error("Error fetching databases:", error?.response?.status, error?.message);
      message.error("Failed to fetch databases");
    }
  };

  // Fetch unique VMs
  // const fetchUniqueVMs = async () => {
  //   try {
  //     const payload = {
  //       orgId: ORG_ID(),
  //       tenantId: DOMAIN_ID(),
  //       limit: 100,
  //       pageId: 1,
  //       filters: [],
  //     };

  //     const res = await axios.post(
  //       `${REACT_APP_API}/api/v1/databaseService/listUniqueHosts`,
  //       payload,
  //       {
  //         headers: {
  //           withCredentials: true,
  //           "X-Authorization": AUTH_TOKEN(),
  //         },
  //       }
  //     );

  //     if (res.data?.data) {
  //       setVmList(res.data.data.map((host) => ({ value: host, label: host })));
  //     }
  //   } catch (error) {
  //     console.error("Error fetching VMs:", error);
  //     message.error("Failed to fetch VMs");
  //   }
  // };

  // Load tables for whichever DB is selected
  const loadTables = async (dbName) => {
    // if (!selectedVM || !dbName) {
    //   console.log("Missing required data:", { selectedVM, dbName });
    //   message.warning("Please select both VM and database");
    //   return;
    // }

    setLoading(true);
    try {
      const payload = {
        orgId: ORG_ID(),
        tenantId: DOMAIN_ID(),
        dbName: dbName || "",
        host: selectedVM || "",
      };

      const res = await axios.post(`${REACT_APP_API}/api/v1/databaseService/listTables`, payload, {
        headers: {
          withCredentials: true,
          "X-Authorization": AUTH_TOKEN(),
        },
      });

      if (res.data?.data) {
        // Convert the response data into the format we need
        const tablesData = Object.entries(res.data.data).map(([tableName, fields]) => ({
          key: tableName,
          tableName: tableName,
          fields: fields,
        }));
        setTablesForSelectedDb(tablesData);
      } else {
        console.log("No tables data in response");
        setTablesForSelectedDb([]);
      }
    } catch (error) {
      console.error("Error loading tables:", error?.response?.status, error?.message);
      message.error("Failed to load tables");
    } finally {
      setLoading(false);
    }
  };

  // Show table details modal with fields
  const showTableDetailsModal = (tableName, fields) => {
    Modal.info({
      title: (
        <div className="flex items-center gap-2">
          <DatabaseOutlined />
          <span>Table Details: {tableName}</span>
        </div>
      ),
      width: 600,
      okText: "Close",
      className: "table-details-modal",
      content: (
        <div className="mt-4">
          <h3 className="font-semibold mb-4 text-lg">Fields</h3>
          <div className="grid grid-cols-2 gap-3">
            {fields.map((field, index) => (
              <div
                key={index}
                className="p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 transition-colors"
              >
                <span className="text-gray-700">{field}</span>
              </div>
            ))}
          </div>
        </div>
      ),
    });
  };

  // Generate a dynamic policy name
  const generatePolicyName = () => {
    const prefix = "db-";
    const characters = "0123456789";
    let result = prefix;
    const sections = [7, 8];

    for (let sectionLength of sections) {
      for (let i = 0; i < sectionLength; i++) {
        const randomCharIndex = Math.floor(Math.random() * characters.length);
        result += characters.charAt(randomCharIndex);
      }
      if (sectionLength !== 8) {
        result += "-";
      }
    }
    return result;
  };

  // Fetch table fields for the selected tables
  const fetchTableFields = async (dbName, tables) => {
    try {
      const payload = {
        orgId: ORG_ID(),
        tenantId: DOMAIN_ID(),
        tenantName: dbName,
        tableNames: tables,
      };

      const res = await axios.post(
        `${REACT_APP_API}/api/v1/databaseService/getTableFields`,
        payload,
        {
          headers: {
            withCredentials: true,
            "X-Authorization": AUTH_TOKEN(),
          },
        }
      );

      if (res.data?.fields) {
        return res.data.fields;
      }
      return {};
    } catch (error) {
      console.error("Error fetching table fields:", error?.response?.status, error?.message);
      message.error("Failed to fetch table fields");
      return {};
    }
  };

  // Create policy for a single table
  const handleGrantAccessForTable = async (tableName) => {
    if (!selectedDatabase) {
      message.warning("Please select a database first");
      return;
    }

    // Show loading
    message.loading({ content: "Preparing policy creation...", key: "tableFields" });

    // Fetch fields for the selected table
    const fields = await fetchTableFields(selectedDatabase, [tableName]);

    // Create an initial field masking object (empty arrays for each table)
    const initialFieldMasking = {};
    Object.keys(fields).forEach((table) => {
      initialFieldMasking[table] = [];
    });

    // Generate a policy name
    const policyName = generatePolicyName();

    localStorage.setItem(
      "policyDraft.basicPolicyInformation",
      JSON.stringify({
        policyType: "database",
        policyName: policyName,
        policyJson: {
          policyName: policyName,
          policyType: "database",
          database: {
            database_name: selectedDatabase,
            host: selectedVM,
            tables: [tableName],
            field_masking: initialFieldMasking,
          },
        },
        // Store the fields data to be used when the form loads
        fieldsData: fields,
      })
    );

    message.success({ content: "Ready to create policy", key: "tableFields" });
    navigate("/pam/policies/database/new", {
      state: { databasePrefill: { host: selectedVM, database: selectedDatabase, tables: [tableName] } },
    });
  };

  // Create policy for multiple selected tables
  const handleGrantAccessForSelectedTables = async () => {
    if (!selectedDatabase) {
      message.warning("Please select a database first");
      return;
    }

    if (selectedTables.length === 0) {
      message.warning("Please select at least one table");
      return;
    }

    // Show loading
    message.loading({ content: "Preparing policy creation...", key: "tableFields" });

    // Fetch fields for all selected tables
    const fields = await fetchTableFields(selectedDatabase, selectedTables);

    // Create an initial field masking object (empty arrays for each table)
    const initialFieldMasking = {};
    Object.keys(fields).forEach((table) => {
      initialFieldMasking[table] = [];
    });

    // Generate a policy name
    const policyName = generatePolicyName();

    localStorage.setItem(
      "policyDraft.basicPolicyInformation",
      JSON.stringify({
        policyType: "database",
        policyName: policyName,
        policyJson: {
          policyName: policyName,
          policyType: "database",
          database: {
            database_name: selectedDatabase,
            host: selectedVM,
            tables: selectedTables,
            field_masking: initialFieldMasking,
          },
        },
        // Store the fields data to be used when the form loads
        fieldsData: fields,
      })
    );

    message.success({ content: "Ready to create policy", key: "tableFields" });
    navigate("/pam/policies/database/new", {
      state: { databasePrefill: { host: selectedVM, database: selectedDatabase, tables: selectedTables } },
    });
  };

  const rowSelection = {
    selectedRowKeys: selectedTables,
    onChange: (selectedRowKeys) => {
      setSelectedTables(selectedRowKeys);
    },
  };

  const columns = [
    {
      title: "Table",
      dataIndex: "tableName",
      key: "tableName",
      render: (text, record) => (
        <button
          type="button"
          onClick={() => showTableDetailsModal(text, record.fields)}
          style={{
            border: "none",
            background: "transparent",
            padding: 0,
            fontFamily: AN_MONO,
            fontSize: 12.5,
            fontWeight: 500,
            color: AN_COLORS.primary,
            cursor: "pointer",
          }}
        >
          {text}
        </button>
      ),
    },
    {
      title: "Fields",
      dataIndex: "fields",
      key: "fieldCount",
      width: 110,
      render: (fields) => (
        <span
          style={{
            fontFamily: AN_MONO,
            fontSize: 12,
            color: AN_COLORS.ink3,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {fields?.length ?? 0}
        </span>
      ),
    },
    {
      title: "",
      key: "action",
      width: 140,
      align: "right",
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => handleGrantAccessForTable(record.tableName)}
          disabled={selectedTables.length > 0}
        >
          Grant access
        </Button>
      ),
    },
  ];

  const appliedFilters = [
    selectedVM && {
      key: "host",
      label: `Host · ${selectedVM}`,
      clear: () => setSelectedVM(null),
    },
    selectedDatabase && {
      key: "database",
      label: `Database · ${selectedDatabase}`,
      clear: () => setSelectedDatabase(null),
    },
  ].filter(Boolean);

  return (
    <EmbeddedShell>
      {selectedTables.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: "10px 14px",
            marginBottom: 14,
            background: AN_COLORS.tint,
            border: `1px solid ${anAlpha(AN_COLORS.primary, 13)}`,
            borderRadius: 10,
          }}
        >
          <span style={{ fontSize: 12.5, color: AN_COLORS.ink2 }}>
            <strong style={{ fontFamily: AN_MONO, color: AN_COLORS.primary }}>
              {selectedTables.length}
            </strong>{" "}
            {selectedTables.length === 1 ? "table" : "tables"} selected
          </span>
          <span style={{ display: "flex", gap: 8 }}>
            <Button size="small" onClick={() => setSelectedTables([])}>
              Clear selection
            </Button>
            <Button type="primary" size="small" onClick={handleGrantAccessForSelectedTables}>
              Grant access to selected
            </Button>
          </span>
        </div>
      )}

      <FilterCard columns="1fr 1fr" applied={appliedFilters} showing={tablesForSelectedDb.length}>
        <FilterField label="Host">
          <Select
            style={{ width: "100%", height: 36 }}
            placeholder="Select a host"
            showSearch
            allowClear
            value={selectedVM}
            onChange={(value) => setSelectedVM(value ?? null)}
            options={vmList}
          />
        </FilterField>
        <FilterField label="Database">
          <Select
            style={{ width: "100%", height: 36 }}
            placeholder="Select a database"
            showSearch
            allowClear
            value={selectedDatabase}
            onChange={(dbName) => setSelectedDatabase(dbName ?? null)}
            options={dbList}
            suffixIcon={<DatabaseOutlined />}
            disabled={!selectedVM}
          />
        </FilterField>
      </FilterCard>

      <TableCard>
        <Table
          {...anTableProps}
          rowSelection={rowSelection}
          columns={columns}
          dataSource={tablesForSelectedDb}
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          locale={tableEmpty(
            loading,
            selectedDatabase
              ? "No tables found in this database."
              : "Select a host and database to list its tables."
          )}
        />
      </TableCard>
    </EmbeddedShell>
  );
};

export default TablesScreen;
